import { useEffect, useRef } from 'react';
import type { MerchantPreset } from '../merchants/types';

interface AutoOpenConfig {
  enabled: boolean;
  delayMs?: number;
  pageRules?: AutoOpenPageRuleConfig[];
  scrollPercent?: number;
  exitIntent?: boolean;
  idleMs?: number;
  /** Maximum number of automatic opens per visitor. Defaults to 1. */
  maxOpensPerSession?: number;
  disableOnMobile?: boolean;
}

interface AutoOpenPageRuleConfig {
  path?: string;
  paths?: string[];
  match?: 'exact' | 'prefix' | 'contains' | 'regex';
  delayMs?: number;
}

interface UseAutoOpenOptions {
  /** When `open` is controlled externally, skip auto-open entirely */
  controlledOpen: boolean | undefined;
  config: AutoOpenConfig | null | undefined;
  minimized: boolean;
  domainAllowed: boolean;
  openChat: () => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  /** Active merchant preset; `autoOpen.disableOnMobile` forces the mobile opt-out. */
  merchantPreset?: Pick<MerchantPreset, 'autoOpen'> | null;
}

const STORAGE_KEY = 'rw_auto_open_count';
const DISMISSED_KEY = 'rw_auto_open_dismissed';
const DEFAULT_MAX_OPENS_PER_SESSION = 1;
const MOBILE_MEDIA_QUERY =
  '(max-width: 640px), (max-width: 1024px) and (max-height: 640px) and (pointer: coarse)';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Last-resort store when neither localStorage nor sessionStorage is usable. */
const memoryStore = new Map<string, string>();
const memoryStorage: StorageLike = {
  getItem: key => memoryStore.get(key) ?? null,
  setItem: (key, value) => {
    memoryStore.set(key, value);
  },
  removeItem: key => {
    memoryStore.delete(key);
  },
};

function storageWorks(storage: StorageLike | null | undefined): storage is StorageLike {
  if (!storage) return false;
  try {
    const probe = '__rw_auto_open_probe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function pickStorage(kind: 'persistent' | 'session'): StorageLike {
  if (typeof window === 'undefined') return memoryStorage;
  const candidates: Array<() => StorageLike | null | undefined> =
    kind === 'persistent'
      ? [() => window.localStorage, () => window.sessionStorage]
      : [() => window.sessionStorage];
  for (const getCandidate of candidates) {
    try {
      const candidate = getCandidate();
      if (storageWorks(candidate)) return candidate;
    } catch {
      // Accessing storage itself can throw (e.g. sandboxed iframes); try the next one
    }
  }
  return memoryStorage;
}

function getHostSuffix(): string {
  if (typeof window === 'undefined') return 'default';
  return window.location?.hostname || window.location?.host || 'default';
}

function getStorageKey(): string {
  if (typeof window === 'undefined') return STORAGE_KEY;
  return `${STORAGE_KEY}_${getHostSuffix()}`;
}

function getDismissedKey(): string {
  return `${DISMISSED_KEY}_${getHostSuffix()}`;
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  }
  return window.innerWidth <= 640;
}

function shouldDisableForMobile(
  config: AutoOpenConfig,
  merchantPreset?: Pick<MerchantPreset, 'autoOpen'> | null
): boolean {
  if (!isMobileViewport()) return false;
  return config.disableOnMobile === true || merchantPreset?.autoOpen?.disableOnMobile === true;
}

function getCount(): number {
  try {
    const store = pickStorage('persistent');
    const rawHost = store.getItem(getStorageKey());
    if (rawHost) return Number(rawHost) || 0;
    const rawLegacy = store.getItem(STORAGE_KEY);
    return rawLegacy ? Number(rawLegacy) || 0 : 0;
  } catch {
    return 0;
  }
}

function incrementCount(): void {
  try {
    const store = pickStorage('persistent');
    const hostKey = getStorageKey();
    const raw = store.getItem(hostKey);
    const next = (Number(raw) || 0) + 1;
    store.setItem(hostKey, String(next));
    if (hostKey !== STORAGE_KEY) {
      store.setItem(STORAGE_KEY, String(next));
    }
  } catch {
    // ignore storage errors
  }
}

function isDismissed(): boolean {
  try {
    return pickStorage('session').getItem(getDismissedKey()) === '1';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    pickStorage('session').setItem(getDismissedKey(), '1');
  } catch {
    // ignore storage errors
  }
}

function normalizePath(path: string): string {
  const normalized = path.replace(/\/+$/, '');
  return normalized || '/';
}

function pathMatchesRule(path: string, rule: AutoOpenPageRuleConfig): boolean {
  const candidates = [rule.path, ...(Array.isArray(rule.paths) ? rule.paths : [])].filter(
    (item): item is string => typeof item === 'string' && item.length > 0
  );
  if (candidates.length === 0) return false;

  const currentPath = normalizePath(path);
  const match = rule.match || 'exact';

  return candidates.some(candidate => {
    const target = normalizePath(candidate);
    if (match === 'prefix') return currentPath === target || currentPath.startsWith(`${target}/`);
    if (match === 'contains') return currentPath.includes(target);
    if (match === 'regex') {
      try {
        return new RegExp(candidate).test(currentPath);
      } catch {
        return false;
      }
    }
    return currentPath === target;
  });
}

function getPageRuleDelay(config: AutoOpenConfig): number | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!Array.isArray(config.pageRules)) return undefined;

  const path = window.location?.pathname || '/';
  const rule = config.pageRules.find(item => pathMatchesRule(path, item));
  return typeof rule?.delayMs === 'number' && rule.delayMs > 0 ? rule.delayMs : undefined;
}

export function useAutoOpen({
  controlledOpen,
  config,
  minimized,
  domainAllowed,
  openChat,
  trackAnalytics,
  merchantPreset,
}: UseAutoOpenOptions): void {
  const autoOpenedRef = useRef(false);

  // When the widget is minimized again after we auto-opened it, the visitor closed it.
  // Remember that for the rest of the session so no trigger re-opens it.
  useEffect(() => {
    if (minimized && autoOpenedRef.current) {
      autoOpenedRef.current = false;
      markDismissed();
    }
  }, [minimized]);

  useEffect(() => {
    if (controlledOpen !== undefined) return;
    if (!config || !config.enabled) return;
    if (!minimized) return;
    if (!domainAllowed) return;
    if (shouldDisableForMobile(config, merchantPreset)) return;
    if (isDismissed()) return;

    const maxOpens =
      typeof config.maxOpensPerSession === 'number'
        ? config.maxOpensPerSession
        : DEFAULT_MAX_OPENS_PER_SESSION;

    let triggered = false;
    const triggerOpen = (reason: string) => {
      if (triggered) return;
      if (shouldDisableForMobile(config, merchantPreset)) return;
      if (isDismissed()) return;
      if (getCount() >= maxOpens) return;
      triggered = true;
      autoOpenedRef.current = true;
      incrementCount();
      openChat();
      trackAnalytics('auto_open', { reason });
    };

    const cleanups: Array<() => void> = [];
    const pageRuleDelayMs = getPageRuleDelay(config);
    const delayMs = pageRuleDelayMs ?? config.delayMs;

    if (typeof delayMs === 'number' && delayMs > 0) {
      const id = setTimeout(
        () => triggerOpen(pageRuleDelayMs === undefined ? 'delay' : 'page_delay'),
        delayMs
      );
      cleanups.push(() => clearTimeout(id));
    }

    if (typeof config.scrollPercent === 'number') {
      const handler = () => {
        const doc = document.documentElement;
        const scrollTop = window.pageYOffset || doc.scrollTop || 0;
        const scrollHeight = doc.scrollHeight - doc.clientHeight;
        const percent = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        if (percent >= config.scrollPercent!) {
          triggerOpen('scroll');
        }
      };
      window.addEventListener('scroll', handler);
      cleanups.push(() => window.removeEventListener('scroll', handler));
    }

    if (config.exitIntent) {
      const handler = (event: MouseEvent) => {
        // Only a genuine exit: pointer left the document upward, not into another element
        const legacy = event as MouseEvent & { toElement?: Element | null };
        if (event.clientY <= 0 && !event.relatedTarget && !legacy.toElement) {
          triggerOpen('exit_intent');
        }
      };
      document.addEventListener('mouseout', handler);
      cleanups.push(() => document.removeEventListener('mouseout', handler));
    }

    if (typeof config.idleMs === 'number' && config.idleMs > 0) {
      let idleTimer: number | undefined;
      const resetTimer = () => {
        if (idleTimer) window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => triggerOpen('idle'), config.idleMs);
      };
      const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'scroll', 'touchstart'];
      events.forEach(evt => window.addEventListener(evt, resetTimer));
      resetTimer();
      cleanups.push(() => {
        events.forEach(evt => window.removeEventListener(evt, resetTimer));
        if (idleTimer) window.clearTimeout(idleTimer);
      });
    }

    return () => {
      cleanups.forEach(fn => fn());
    };
  }, [controlledOpen, config, minimized, domainAllowed, openChat, trackAnalytics, merchantPreset]);
}
